import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';

interface BlogPost {
    _id: string;
    title: { en: string; sv: string; da?: string };
    content: { en: string; sv: string; da?: string };
    excerpt: { en: string; sv: string; da?: string };
    coverImage: string;
    category: string;
    tags: string[];
    slug: string;
    readTime: number;
    publishedAt: string;
    images?: { url: string; alt: string; caption: string }[];
    seo?: { metaTitle: { en: string; sv: string }; metaDescription: { en: string; sv: string } };
}

const CATEGORY_COLORS: Record<string, string> = {
    beach: 'from-cyan-500 to-blue-500',
    culture: 'from-amber-500 to-orange-500',
    adventure: 'from-green-500 to-emerald-600',
    luxury: 'from-purple-500 to-pink-500',
    food: 'from-red-500 to-rose-500',
    nightlife: 'from-indigo-500 to-violet-500',
    general: 'from-slate-500 to-gray-600',
};

const CATEGORY_LABELS: Record<string, { en: string; sv: string; da: string }> = {
    beach: { en: 'Beaches', sv: 'Stränder', da: 'Strande' },
    culture: { en: 'Culture', sv: 'Kultur', da: 'Kultur' },
    adventure: { en: 'Adventure', sv: 'Äventyr', da: 'Eventyr' },
    luxury: { en: 'Luxury', sv: 'Lyx', da: 'Luksus' },
    food: { en: 'Food', sv: 'Mat', da: 'Mad' },
    nightlife: { en: 'Nightlife', sv: 'Nattliv', da: 'Natteliv' },
    general: { en: 'General', sv: 'Allmänt', da: 'Generelt' },
};

// Blog List Page
export const BlogListPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { language } = useLocalization();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [destName, setDestName] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | null>(null);

    const getText = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        return val[language] || val.en || val.sv || '';
    };

    useEffect(() => {
        const fetchPosts = async () => {
            setLoading(true);
            try {
                const url = activeCategory
                    ? `/api/destination-blog/public/${slug}?category=${activeCategory}`
                    : `/api/destination-blog/public/${slug}`;
                const res = await fetch(url);
                const data = await res.json();
                if (data.success) {
                    setPosts(data.posts || []);
                    setDestName(data.destination?.name || slug || '');
                }
            } catch (err) {
                console.error('[Blog] Failed to load posts:', err);
            }
            setLoading(false);
        };
        fetchPosts();
    }, [slug, activeCategory]);

    // SEO
    useEffect(() => {
        const title = language === 'sv'
            ? `Reseblogg — ${destName} | Lumi Tours`
            : `Travel Blog — ${destName} | Lumi Tours`;
        document.title = title;
    }, [destName, language]);

    const categories = ['beach', 'culture', 'adventure', 'luxury', 'food', 'nightlife', 'general'];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-20 px-4">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySC0yNHYtMmgxMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
                <div className="container mx-auto max-w-5xl relative z-10">
                    {/* Breadcrumbs */}
                    <div className="flex items-center gap-2 text-blue-200 text-sm mb-6">
                        <Link to="/" className="hover:text-white transition-colors">Lumi Tours</Link>
                        <span>›</span>
                        <Link to={`/destination/${slug}`} className="hover:text-white transition-colors">{destName}</Link>
                        <span>›</span>
                        <span className="text-white font-medium">Blog</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                        {language === 'sv' ? `Reseguide: ${destName}` : `Travel Guide: ${destName}`}
                    </h1>
                    <p className="text-blue-200 text-lg max-w-2xl">
                        {language === 'sv'
                            ? `Upptäck ${destName} genom våra inspirerande resereportage`
                            : `Discover ${destName} through our inspiring travel stories`}
                    </p>
                </div>
            </div>

            {/* Category Filter */}
            <div className="container mx-auto max-w-5xl px-4 -mt-6 relative z-20">
                <div className="bg-white rounded-2xl shadow-lg p-4 flex gap-2 overflow-x-auto">
                    <button
                        onClick={() => setActiveCategory(null)}
                        className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                            !activeCategory
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        {language === 'sv' ? 'Alla' : 'All'}
                    </button>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                activeCategory === cat
                                    ? `bg-gradient-to-r ${CATEGORY_COLORS[cat]} text-white shadow-md`
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {CATEGORY_LABELS[cat]?.[language as 'en' | 'sv' | 'da'] || CATEGORY_LABELS[cat]?.en || cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Posts Grid */}
            <div className="container mx-auto max-w-5xl px-4 py-12">
                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                                <div className="h-48 bg-gray-200" />
                                <div className="p-5 space-y-3">
                                    <div className="h-4 bg-gray-200 rounded w-20" />
                                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                                    <div className="h-4 bg-gray-200 rounded w-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-20">
                        <span className="material-icons text-6xl text-gray-300">article</span>
                        <p className="text-gray-500 mt-4 text-lg">
                            {language === 'sv' ? 'Inga blogginlägg ännu' : 'No blog posts yet'}
                        </p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {posts.map(post => (
                            <article
                                key={post._id}
                                onClick={() => navigate(`/destination/${slug}/blog/${post.slug}`)}
                                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group transform hover:-translate-y-1"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={post.coverImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600'}
                                        alt={getText(post.title)}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute top-3 left-3">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general}`}>
                                            {CATEGORY_LABELS[post.category]?.[language as 'en' | 'sv' | 'da'] || post.category}
                                        </span>
                                    </div>
                                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                                        <span className="material-icons text-xs">schedule</span>
                                        {post.readTime} min
                                    </div>
                                </div>
                                <div className="p-5">
                                    <h2 className="text-lg font-bold text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                                        {getText(post.title)}
                                    </h2>
                                    <p className="text-sm text-gray-500 line-clamp-3 mb-3">
                                        {getText(post.excerpt)}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <time className="text-xs text-gray-400">
                                            {new Date(post.publishedAt).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </time>
                                        <span className="text-blue-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                                            {language === 'sv' ? 'Läs mer' : 'Read more'} →
                                        </span>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Blog Detail Page
export const BlogDetailPage: React.FC = () => {
    const { slug, postSlug } = useParams<{ slug: string; postSlug: string }>();
    const navigate = useNavigate();
    const { language } = useLocalization();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [destName, setDestName] = useState('');

    const getText = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'string') return val;
        return val[language] || val.en || val.sv || '';
    };

    useEffect(() => {
        const fetchPost = async () => {
            setLoading(true);
            try {
                const res = await fetch(`/api/destination-blog/public/slug/${postSlug}`);
                const data = await res.json();
                if (data.success) {
                    setPost(data.post);
                    setDestName(data.destination?.name || slug || '');
                }
            } catch (err) {
                console.error('[Blog] Failed to load post:', err);
            }
            setLoading(false);
        };
        fetchPost();
    }, [postSlug]);

    // SEO
    useEffect(() => {
        if (!post) return;
        const seoTitle = getText(post.seo?.metaTitle) || getText(post.title);
        document.title = `${seoTitle} | Lumi Tours`;
        const desc = getText(post.seo?.metaDescription) || getText(post.excerpt);
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) { meta = document.createElement('meta'); meta.setAttribute('name', 'description'); document.head.appendChild(meta); }
        meta.setAttribute('content', desc);
    }, [post, language]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full" />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <span className="material-icons text-6xl text-gray-300">article</span>
                <p className="text-gray-500 mt-4">Post not found</p>
                <button onClick={() => navigate(`/destination/${slug}/blog`)} className="mt-4 text-blue-600 hover:underline">
                    ← {language === 'sv' ? 'Tillbaka till bloggen' : 'Back to blog'}
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Full-width Hero */}
            <div className="relative h-[50vh] min-h-[400px]">
                <img
                    src={post.coverImage || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200'}
                    alt={getText(post.title)}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 container mx-auto max-w-4xl">
                    {/* Breadcrumbs */}
                    <div className="flex items-center gap-2 text-blue-200 text-sm mb-4">
                        <Link to="/" className="hover:text-white transition-colors">Lumi Tours</Link>
                        <span>›</span>
                        <Link to={`/destination/${slug}`} className="hover:text-white transition-colors">{destName}</Link>
                        <span>›</span>
                        <Link to={`/destination/${slug}/blog`} className="hover:text-white transition-colors">Blog</Link>
                    </div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general} mb-3`}>
                        {CATEGORY_LABELS[post.category]?.[language as 'en' | 'sv' | 'da'] || post.category}
                    </span>
                    <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
                        {getText(post.title)}
                    </h1>
                    <div className="flex items-center gap-4 text-gray-300 text-sm">
                        <span className="flex items-center gap-1">
                            <span className="material-icons text-sm">schedule</span>
                            {post.readTime} min {language === 'sv' ? 'läsning' : 'read'}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="material-icons text-sm">calendar_today</span>
                            {new Date(post.publishedAt).toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto max-w-4xl px-4 py-12">
                <div className="grid lg:grid-cols-[1fr_280px] gap-10">
                    {/* Main Content */}
                    <article className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
                        {/* Excerpt as lead paragraph */}
                        {getText(post.excerpt) && (
                            <p className="text-xl text-gray-600 leading-relaxed mb-8 border-l-4 border-blue-500 pl-6 italic">
                                {getText(post.excerpt)}
                            </p>
                        )}
                        {/* Blog content */}
                        <div
                            className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-p:text-gray-700 prose-a:text-blue-600 prose-img:rounded-xl"
                            dangerouslySetInnerHTML={{ __html: getText(post.content) }}
                        />

                        {/* Tags */}
                        {post.tags && post.tags.length > 0 && (
                            <div className="mt-10 pt-8 border-t border-gray-100">
                                <div className="flex flex-wrap gap-2">
                                    {post.tags.map(tag => (
                                        <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 transition-colors">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Back link */}
                        <div className="mt-8">
                            <button
                                onClick={() => navigate(`/destination/${slug}/blog`)}
                                className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 transition-colors"
                            >
                                ← {language === 'sv' ? 'Alla artiklar' : 'All articles'}
                            </button>
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="space-y-6">
                        {/* Destination CTA */}
                        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
                            <h3 className="font-bold text-lg mb-2">
                                {language === 'sv' ? `Resa till ${destName}?` : `Travel to ${destName}?`}
                            </h3>
                            <p className="text-blue-100 text-sm mb-4">
                                {language === 'sv'
                                    ? 'Hitta paketresor, flyg och hotell'
                                    : 'Find package tours, flights and hotels'}
                            </p>
                            <Link
                                to={`/destination/${slug}`}
                                className="inline-block w-full text-center bg-white text-blue-700 font-bold py-2.5 rounded-xl hover:bg-blue-50 transition-colors"
                            >
                                {language === 'sv' ? 'Se erbjudanden →' : 'View offers →'}
                            </Link>
                        </div>

                        {/* Article images */}
                        {post.images && post.images.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
                                <h4 className="font-bold text-sm text-gray-800">
                                    {language === 'sv' ? 'Bilder' : 'Gallery'}
                                </h4>
                                {post.images.map((img, i) => (
                                    <img
                                        key={i}
                                        src={img.url}
                                        alt={img.alt}
                                        className="w-full rounded-xl object-cover h-40"
                                    />
                                ))}
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default BlogListPage;
